// @flow
import _ from 'lodash';
import { createSelector } from 'reselect';
import type { KeyedCollection, Permission } from '../../types';
import type { State } from './';

export const resolveRootPermission = (
  permissionMap: KeyedCollection,
  permission: Permission
) =>
  permission && permission.parentId
    ? resolveRootPermission(permissionMap, permissionMap[permission.parentId])
    : permission || {};

export const selectPermissions = (state: State) =>
  _.get(state, 'entities.permissions', {});

export const selectUserPermissions = (state: State) =>
  _.get(state, 'entities.userPermissions', {});

export const selectProducts = createSelector(
  [selectPermissions],
  (permissions) =>
    _.pickBy(
      permissions,
      (permission) => permission.type === 'p' && !permission.parentId
    )
);

export const selectExternal = createSelector(
  [selectPermissions],
  (permissions) =>
    _.pickBy(
      permissions,
      (permission) => permission.type === 'x' && !permission.parentId
    )
);

export const selectParentByPermissionId = (state, permissionId) => 
  resolveRootPermission(selectPermissions(state), selectPermissions(state)[permissionId])

export const selectUserPermissionByPermissionId = (state: State, permissionId: string) => {
  const ups = selectUserPermissions(state)
  let up = ups && Object.values(ups).filter(item=> item.permissionId === permissionId).pop();

  if(!up){
    const parentId = selectParentByPermissionId(state, permissionId).id
    up = ups && Object.values(ups).filter(item=> item.permissionId === parentId).pop();
  }
  return up;
}

export const selectUserPermissionByPermissionIds = (state: State, permissionIds: string) => 
  permissionIds.map(id => selectUserPermissionByPermissionId(state, id)).filter(up => up && up.permissionId);

export const selectUsersPermissionIds = createSelector(
  [selectUserPermissions],
  (userPermissions) =>
    _(userPermissions)
      .map((permission) => permission.permissionId)
      .uniq()
      .value()
);


export const selectUsersCurrentPermissions = createSelector(
  [selectPermissions],
  (permissions) =>
    _(permissions)
      .map((permission) => permission)
      .uniq()
      .value()
);

export const selectUsersProducts = createSelector(
  [selectUsersPermissionIds, selectProducts],
  (permissionIds, products) =>
    _(permissionIds)
      .map((id) => products[id] || {})
      .filter((product) => product.id)
      .uniq()
      .value()
);

export const selectUsersExternals = createSelector(
  [selectUsersPermissionIds, selectExternal],
  (permissionIds, externals) =>
    _(permissionIds)
      .map((id) => externals[id] || {})
      .filter((external) => external.id)
      .uniq()
      .value()
);

export const selectPermissionBySlug = (state: State, permissionSlug: string) =>{
    if(!permissionSlug) return null; 
    return _.find(
      selectPermissions(state),
      (permission) =>
        permission && permission.code &&
        permission.code.toLowerCase().trim() ===
          permissionSlug.toLowerCase().trim()
    ) || {};
  }
export const selectPermissionById = (state: State, permissionId: string) =>
  selectPermissions(state)[permissionId] || {};

export const selectPermissionsByProductId = (state: State, productId: string) =>
  _(selectPermissions(state))
    .values()
    .filter((permission) => permission.parentId === productId)
    .value();

export const selectPermissionChannels = createSelector(
  [selectPermissionById],
  (permission) => permission.channels
);

export const selectUserPermissionChannels = createSelector(
  [selectPermissionById],
  (permission) =>
    _(permission.allowed)
      .map((allowed, type) => ({
        type,
        displayName: _.capitalize(type),
        allowed: allowed || false
      }))
      .value()
);

export const selectGMParent = createSelector(
  [selectPermissions],
  (permissions) =>
    _(permissions)
      .filter((permission) => permission.type === 'm' && !permission.parentId)
      .value()[0]
);
